/**
 * Pure helpers for the multi-instance Brochure Wizard. Kept separate from the
 * React component so they can be unit-tested without jsdom/Apollo setup, per
 * AGENTS.md.
 */

import { BROCHURE_PAGES, isBrochurePageEnabled } from "./BrochureImageSlots";

/**
 * The wizard resolves the cohort to concrete instances via `instancesV2`, so
 * cap how many it will load. Matches the brochure's queued
 * `maxEntitiesQueued`.
 */
export const WIZARD_INSTANCE_LIMIT = 500;

/**
 * Pure: pick just the sort fields out of an instance search input so a
 * selected-ids cohort can be ordered "in the order of the search" without
 * re-applying the search's filters (which might no longer match every
 * selected instance).
 */
export const extractSearchSorts = search => {
  if (search == null || typeof search !== "object" || Array.isArray(search)) {
    return {};
  }
  return ["sort1", "sort2", "sort3"].reduce(
    (accumulator, key) =>
      search[key] != null
        ? { ...accumulator, [key]: search[key] }
        : accumulator,
    {},
  );
};

/**
 * Pure: read an id off a list item's `meta` (or anywhere else it arrives as a
 * string), returning null for anything that isn't one.
 */
const parseId = value => {
  const id = Number.parseInt(value, 10);
  return Number.isInteger(id) ? id : null;
};

/**
 * Pure: the instance ids a saved list holds, in list order.
 *
 * A list item points at its entity through its `meta` rather than its own id —
 * the same place the server's `resolveInstanceIds` reads a list cohort from —
 * so a list resolves to the same instances whichever path generates the
 * brochure.
 */
export const instanceIdsFromListItems = (edges = []) => [
  ...new Set(
    edges.map(edge => parseId(edge?.meta?.id)).filter(id => id != null),
  ),
];

/**
 * Pure: build the `instancesV2` input that loads the wizard's cohort in
 * search order.
 *
 *   - artworkIds          -> every instance under those artworks (reduced to
 *                            one per artwork by
 *                            `mostRecentInstanceEdgePerArtwork`);
 *   - exhibitionIds       -> every instance hung in those exhibitions;
 *   - listItemIds         -> the instances a saved list holds, which are the
 *                            cohort whatever search is still on screen;
 *   - select "all"        -> the full search (filters + sorts) so the cohort
 *                            is exactly the search result, in its order;
 *   - explicit ids        -> id-in filter + the search's sort fields, so the
 *                            database returns the selection in search order.
 *
 * The artwork / exhibition filters and the saved list's precedence over a
 * search mirror the server's `resolveInstanceIds`, so a cohort picked in
 * Artworks, Exhibitions or a select list resolves to the same instances
 * whichever path generates it.
 *
 * Returns null when the cohort can't be resolved client-side (nothing
 * selected and no search) so the caller can show a helpful message instead
 * of querying.
 */
export const buildWizardInstancesInput = ({
  ids = [],
  artworkIds = [],
  exhibitionIds = [],
  listItemIds = [],
  search,
  select,
} = {}) => {
  if (Array.isArray(artworkIds) && artworkIds.length > 0) {
    return {
      _edition: { artworkId: { value: artworkIds, operator: "in" } },
      limit: WIZARD_INSTANCE_LIMIT,
    };
  }
  if (Array.isArray(exhibitionIds) && exhibitionIds.length > 0) {
    return {
      _exhibitionWork: {
        _exhibition: { id: { value: exhibitionIds, operator: "in" } },
      },
      limit: WIZARD_INSTANCE_LIMIT,
    };
  }
  if (Array.isArray(listItemIds) && listItemIds.length > 0) {
    return {
      id: { value: listItemIds, operator: "in" },
      limit: Math.min(listItemIds.length, WIZARD_INSTANCE_LIMIT),
    };
  }
  if (select === "all" && search != null) {
    return { ...search, limit: WIZARD_INSTANCE_LIMIT };
  }
  if (Array.isArray(ids) && ids.length > 0) {
    return {
      ...extractSearchSorts(search),
      id: { value: ids, operator: "in" },
      limit: Math.min(ids.length, WIZARD_INSTANCE_LIMIT),
    };
  }
  return null;
};

/**
 * Pure: reduce an artwork-driven cohort to one instance per artwork — the most
 * recently created one, i.e. the highest instance id.
 *
 * Every instance of an artwork carries the same images, title, medium and
 * dimensions (images hang off the artwork), so a brochure page per instance
 * would just repeat itself. The instance is only there to supply the reference
 * and edition line. Artworks are returned in the order they were selected.
 *
 * Mirrors `pickMostRecentInstanceId` on the server.
 */
export const mostRecentInstanceEdgePerArtwork = (
  edges = [],
  artworkIds = [],
) => {
  const bestByArtworkId = edges.reduce((accumulator, edge) => {
    const artworkId = edge?.edition?.artwork?.id;
    if (artworkId == null || edge?.id == null) return accumulator;
    const current = accumulator.get(artworkId);
    if (current == null || edge.id > current.id)
      accumulator.set(artworkId, edge);
    return accumulator;
  }, new Map());

  // Selection order first, then any artwork the query returned that wasn't in
  // the requested list (defensive — keeps a cohort from silently shrinking).
  const requested = artworkIds
    .map(artworkId => bestByArtworkId.get(artworkId))
    .filter(Boolean);
  const requestedIds = new Set(requested.map(edge => edge.id));
  return [
    ...requested,
    ...Array.from(bestByArtworkId.values()).filter(
      edge => !requestedIds.has(edge.id),
    ),
  ];
};

/**
 * Pure: the edges the wizard should actually work with. Artwork-driven cohorts
 * collapse to one instance per artwork; everything else is already the cohort.
 */
export const selectWizardEdges = ({ edges = [], artworkIds = [] } = {}) =>
  Array.isArray(artworkIds) && artworkIds.length > 0
    ? mostRecentInstanceEdgePerArtwork(edges, artworkIds)
    : edges;

/**
 * Pure: shape an `instancesV2` edge into the summary the wizard renders —
 * name, artist, title and the artwork's ordered images (primary first).
 */
export const instanceSummaryFromEdge = edge => {
  const artwork = edge?.edition?.artwork || null;
  const images = [...(artwork?.images || [])].sort(
    (a, b) => (a?.order ?? Infinity) - (b?.order ?? Infinity),
  );
  return {
    id: edge?.id ?? null,
    name: edge?.name || "",
    title: artwork?.title || "",
    series: artwork?.series || "",
    precis: artwork?.precis || "",
    artistName: (artwork?.artists || [])
      .map(artist => artist?.name)
      .filter(Boolean)
      .join(", "),
    artists: (artwork?.artists || []).filter(artist => artist?.id != null),
    year: edge?.edition?.year || "",
    thumbnail: images[0]?.imgT || null,
    images,
  };
};

/**
 * Pure: Map<id, summary> for every edge with an id.
 */
export const instanceSummariesById = (edges = []) =>
  new Map(
    edges
      .map(instanceSummaryFromEdge)
      .filter(summary => summary.id != null)
      .map(summary => [summary.id, summary]),
  );

/**
 * Pure: the wizard's initial instance order.
 *
 * A cohort that arrives in an order of its own — a resumed draft's saved order,
 * or a select list's own order — keeps it, with any instances that no longer
 * resolve dropped and any newly-found ones appended. Everything else takes the
 * fetched edges' order, i.e. the order of the search.
 */
export const initialInstanceOrder = ({
  edges = [],
  preferredOrder = null,
} = {}) => {
  const fetchedIds = edges.map(edge => edge?.id).filter(id => id != null);
  if (!Array.isArray(preferredOrder) || preferredOrder.length === 0) {
    return fetchedIds;
  }

  const fetched = new Set(fetchedIds);
  const kept = preferredOrder.filter(id => fetched.has(id));
  const keptSet = new Set(kept);
  return [...kept, ...fetchedIds.filter(id => !keptSet.has(id))];
};

/**
 * The pages a work's section can hold, in the order a default section prints
 * them. The report-level front cover isn't one of them: the wizard edits and
 * switches it separately. Mirrors the server's `FIXED_RUN_TEMPLATES`.
 */
const FIXED_RUN_TEMPLATES = BROCHURE_PAGES.filter(page => page.id !== "cover");

/**
 * Four images in a two-by-two grid, on the same margins as Two Details. The
 * cells are portrait; a landscape image shows the central third of its width
 * instead of being squeezed in whole.
 *
 * Filled from the work's primary image it would print the same picture four
 * times over, so it is only ever added to a section by hand — which is why it
 * isn't one of the pages above. Mirrors the server's
 * `ARRANGED_ONLY_PAGE_TEMPLATES`.
 */
export const FOUR_DETAILS_TEMPLATE = {
  id: "fourDetails",
  label: "Four Details",
  slots: [
    { key: "fourDetailsTopLeft", label: "Top left (portrait)" },
    { key: "fourDetailsTopRight", label: "Top right (portrait)" },
    { key: "fourDetailsBottomLeft", label: "Bottom left (portrait)" },
    { key: "fourDetailsBottomRight", label: "Bottom right (portrait)" },
  ],
};

/**
 * A page of the user's own words: a title, a subtitle, body copy and one
 * optional image, above the words or below them. It has nothing to print until
 * someone writes it, so it is only ever added to a section by hand — which is
 * why it isn't one of the pages above.
 */
export const FREE_TEXT_TEMPLATE = {
  id: "freeText",
  label: "Free Text",
  description: "Your own title, subtitle and body copy, with one image.",
  slots: [
    {
      key: "freeText",
      label: "Image (optional)",
      optional: true,
    },
  ],
};

/**
 * Where a Free Text page's image sits. Mirrors the server's
 * `FREE_TEXT_IMAGE_POSITIONS`.
 */
export const FREE_TEXT_IMAGE_POSITIONS = [
  { id: "bottom", label: "Bottom of page" },
  { id: "top", label: "Above the text" },
];

/**
 * The pages a work's section can hold, in the order the picker offers them: the
 * fixed run, with Four Details beside the Two Details it extends, and Free Text
 * last. Mirrors the server's `SECTION_PAGE_TEMPLATES`.
 */
export const SECTION_TEMPLATES = [
  ...FIXED_RUN_TEMPLATES.flatMap(template =>
    template.id === "landscapes"
      ? [template, FOUR_DETAILS_TEMPLATE]
      : [template],
  ),
  FREE_TEXT_TEMPLATE,
];

const templateDefinition = template =>
  SECTION_TEMPLATES.find(entry => entry.id === template) || null;

/**
 * Pure: a template's name, e.g. "Two Details".
 */
export const templateLabel = template =>
  templateDefinition(template)?.label || "Page";

/**
 * Pure: what a template prints, for the pages that need saying (the ones that
 * expand to a page per artist). Blank for the image-led pages.
 */
export const templateDescription = template =>
  templateDefinition(template)?.description || "";

/**
 * Pure: the image slots a page of this template fills. Biography and Selected
 * Public Collections have none — they are written from the artist's own record.
 */
export const templateSlots = template =>
  templateDefinition(template)?.slots || [];

/**
 * Pure: is this a slot the page can do without? An optional slot left unpicked
 * stays empty, where every other slot falls back to the work's primary image.
 * Mirrors the server's `OPTIONAL_IMAGE_SLOTS`.
 */
export const isOptionalSlot = (template, slotKey) =>
  templateSlots(template).some(slot => slot.key === slotKey && slot.optional);

/**
 * Pure: does this template carry words of its own, which the user writes?
 */
export const isFreeTextTemplate = template =>
  template === FREE_TEXT_TEMPLATE.id;

/**
 * The three shapes an image can be set as. Mirrors the server's
 * `IMAGE_ORIENTATIONS`.
 */
export const IMAGE_ORIENTATIONS = [
  { id: "portrait", label: "Portrait" },
  { id: "landscape", label: "Landscape" },
  { id: "square", label: "Square" },
];

/**
 * The templates that anchor their image by its shape, and so take a setting.
 * Only the Artwork Hero does: it hangs a landscape image from a fixed line and
 * stands a portrait or square one on the bottom margin. Mirrors the server's
 * `IMAGE_ORIENTATION_TEMPLATES`.
 */
const IMAGE_ORIENTATION_TEMPLATES = ["details"];

export const usesImageOrientation = template =>
  IMAGE_ORIENTATION_TEMPLATES.includes(template);

/**
 * Pure: which of the three shapes an image is, from its own dimensions.
 *
 * Anything within 5% of 1:1 is square — a photograph trimmed to 1010x1000 is a
 * square in every way that matters to a layout. Takes an image element's
 * `naturalWidth`/`naturalHeight`, or any `{ width, height }`. Mirrors the
 * renderer's `detectImageOrientation`, so what the user is shown here is what
 * prints.
 */
const SQUARE_ASPECT_TOLERANCE = 0.05;

export const detectImageOrientation = ({
  naturalWidth,
  naturalHeight,
  width,
  height,
} = {}) => {
  const imageWidth = naturalWidth ?? width;
  const imageHeight = naturalHeight ?? height;
  if (!imageWidth || !imageHeight) return "square";
  const aspect = imageWidth / imageHeight;
  if (Math.abs(aspect - 1) <= SQUARE_ASPECT_TOLERANCE) return "square";
  return aspect > 1 ? "landscape" : "portrait";
};

/**
 * Pure: the empty page a Free Text page starts as.
 */
export const emptyFreeTextContent = () => ({
  title: "",
  subtitle: "",
  body: "",
  imagePosition: FREE_TEXT_IMAGE_POSITIONS[0].id,
});

/**
 * Pure: add an artwork's précis to a Free Text body without discarding words
 * already written there. Blank values stay blank and repeated clicks do not
 * duplicate the same précis.
 */
export const appendPrecisToBody = (body = "", precis = "") => {
  const inserted = precis.trim();
  if (!inserted || body.includes(inserted)) return body;
  return body.trim() ? `${body.trimEnd()}\n\n${inserted}` : inserted;
};

/**
 * Pure: one page of a work's section. `key` tells two otherwise identical rows
 * apart while the wizard is open — a section can hold the same template twice,
 * which is the point of arranging one — and isn't saved, since the saved order
 * is the truth.
 */
export const sectionPage = ({
  key,
  template,
  imageSlots = {},
  content,
  imageOrientation,
}) => ({
  key,
  template,
  imageSlots,
  ...(isFreeTextTemplate(template)
    ? { content: { ...emptyFreeTextContent(), ...content } }
    : {}),
  // Unset until an image is picked (or the shape is chosen by hand), which
  // leaves the renderer to read the shape off the image itself.
  ...(usesImageOrientation(template) && imageOrientation
    ? { imageOrientation }
    : {}),
});

/**
 * Pure: the patch picking an image makes to the page it was picked for.
 *
 * The image lands in its slot — or leaves it, since an optional slot clears when
 * its picked image is clicked again, letting a Free Text page go back to being
 * words alone.
 *
 * On a page that anchors its image by shape (the Artwork Hero), the shape
 * follows the image: picking a landscape photograph sets the page to landscape,
 * replacing whatever shape it held before. Someone who picks a different image
 * is choosing a picture, not keeping an old override, and the three shape
 * buttons are there to set it back by hand afterwards. `orientation` is the shape
 * the picked image was read as, which is unknown until its thumbnail has loaded —
 * unset, the page keeps what it had and the renderer reads the shape itself.
 */
export const patchForPickedImage = ({
  page,
  slotKey,
  archiveId,
  orientation = null,
}) => {
  const clearing =
    isOptionalSlot(page?.template, slotKey) &&
    page?.imageSlots?.[slotKey] === archiveId;

  return {
    imageSlots: {
      ...page?.imageSlots,
      [slotKey]: clearing ? undefined : archiveId,
    },
    ...(usesImageOrientation(page?.template) && !clearing && orientation
      ? { imageOrientation: orientation }
      : {}),
  };
};

/**
 * Pure: a page's id for drag-and-drop.
 */
export const sectionPageId = page => `page:${page?.key}`;

/**
 * Pure: number a section's pages from 1, so every row has a stable id from the
 * first render.
 */
const withPageKeys = (pages = []) =>
  pages.map((page, index) => ({ ...page, key: index + 1 }));

/**
 * Pure: the key the next page added to a section should take.
 */
export const nextPageKey = (pages = []) =>
  pages.reduce((highest, page) => Math.max(highest, page.key ?? 0), 0) + 1;

/**
 * Pure: the picks among `slots` from a work's flat image map.
 */
const picksForSlots = (imageSlots, slots = []) =>
  slots.reduce(
    (accumulator, slot) =>
      imageSlots?.[slot.key] != null
        ? { ...accumulator, [slot.key]: imageSlots[slot.key] }
        : accumulator,
    {},
  );

/**
 * Pure: one work's section as the wizard should show it.
 *
 * A work the user has arranged keeps its list. Anything else — an untouched
 * work, or one from a draft saved before sections were arrangeable — has its
 * on/off toggles expanded into a list of the pages that were switched on,
 * carrying its picked images onto the pages that use them. `multiInstance`
 * decides which pages a document of several works starts with.
 */
export const sectionPagesOf = (
  configuration,
  { multiInstance = false } = {},
) => {
  if (configuration?.pages?.length > 0) {
    return withPageKeys(configuration.pages.map(page => sectionPage(page)));
  }
  return withPageKeys(
    FIXED_RUN_TEMPLATES.filter(page =>
      isBrochurePageEnabled(configuration?.brochurePages, page.id, {
        multiInstance,
      }),
    ).map(page =>
      sectionPage({
        template: page.id,
        imageSlots: picksForSlots(configuration?.imageSlots, page.slots),
      }),
    ),
  );
};

/**
 * Pure: put `page` at `index` in a section, clamped to its ends.
 */
export const insertSectionPage = (pages = [], index, page) => {
  const at = Math.max(0, Math.min(index, pages.length));
  return [...pages.slice(0, at), page, ...pages.slice(at)];
};

/**
 * Pure: drop one page from a section.
 */
export const removeSectionPage = (pages = [], id) =>
  pages.filter(page => sectionPageId(page) !== id);

/**
 * Pure: patch one page of a section — its image picks, say.
 */
export const updateSectionPage = (pages = [], id, patch) =>
  pages.map(page =>
    sectionPageId(page) === id ? { ...page, ...patch } : page,
  );

/**
 * Pure: move the item at `from` to `to`, returning a new array. Out-of-range
 * indices return the list unchanged.
 */
export const reorderList = (list = [], from, to) => {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= list.length ||
    to >= list.length
  ) {
    return list;
  }
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * Pure: remove one instance from the report order. The component prevents the
 * final instance being removed; this helper only performs the list operation.
 */
export const removeInstanceFromOrder = (orderedIds = [], instanceId) =>
  orderedIds.filter(id => id !== instanceId);

// ------------------------------------------------- Biography page: the artist
/**
 * Pure: the biography the Biography page starts from — whatever the artist's
 * contact record says. Editing it in the wizard changes this brochure only, so
 * this is only ever the starting point.
 */
export const artistBiography = artist => artist?.contact?.biography || "";

/**
 * Pure: the caption the Biography page starts from, naming the artist. Mirrors
 * what the renderer falls back to, so the caption on screen is the caption that
 * prints — and editing it changes this brochure only.
 */
export const defaultPortraitCaption = artist =>
  `Portrait of ${artist?.name || ""}, courtesy the artist.`;

/**
 * The artist portrait a Biography page can carry: PNG or JPG, no larger than
 * 1MB. The same limit the server signs into the upload URL, checked here so the
 * user hears about it before anything is uploaded.
 */
export const PORTRAIT_MAX_BYTES = 1024 * 1024;
export const PORTRAIT_FILE_TYPES = ["image/png", "image/jpeg"];
export const PORTRAIT_FILE_ACCEPT = ".png,.jpg,.jpeg";

const megabytes = bytes =>
  `${Math.round((bytes / PORTRAIT_MAX_BYTES) * 10) / 10}MB`;

/**
 * Pure: what's wrong with the chosen file, in the words the user needs, or null
 * when there's nothing wrong with it.
 */
export const portraitFileError = file => {
  if (!file) return "Choose a PNG or JPG image.";
  if (!PORTRAIT_FILE_TYPES.includes(file.type)) {
    return "An artist portrait has to be a PNG or a JPG.";
  }
  if (file.size > PORTRAIT_MAX_BYTES) {
    return `That image is ${megabytes(file.size)} — an artist portrait has to be 1MB or smaller.`;
  }
  return null;
};

/**
 * Pure: normalise a draft payload's report cover to the wizard's shape, or
 * null when the draft has none.
 */
const reportCoverFromPayload = payload => {
  const cover = payload?.reportCover;
  if (cover == null || typeof cover !== "object" || Array.isArray(cover)) {
    return null;
  }
  return {
    instanceId: cover.instanceId ?? null,
    archiveId: cover.archiveId ?? null,
    title: cover.title || "",
    subtitle: cover.subtitle || "",
    // Drafts saved before the cover gained explicit dates/location fields used
    // `year`; keep those dates when they are resumed.
    dates: cover.dates || cover.year || "",
    location: cover.location || "",
  };
};

/**
 * Pure: the per-work sections, images and collection overrides a saved draft
 * holds, keyed by instance id.
 */
const configurationsFromDraftPayload = payload =>
  Object.fromEntries(
    (Array.isArray(payload?.instances) ? payload.instances : [])
      .filter(entry => entry?.id != null)
      .map(entry => [
        entry.id,
        {
          imageSlots: entry.imageSlots || {},
          brochurePages: entry.brochurePages || {},
          publicCollections: entry.publicCollections || {},
          // The work's section in order, on drafts saved since sections became
          // arrangeable. Older drafts expand their toggles instead (see
          // `sectionPagesOf`).
          pages: Array.isArray(entry.pages) ? entry.pages : [],
        },
      ]),
  );

/**
 * Pure: an artist-keyed map off a saved payload, or an empty one when the draft
 * hasn't got it (every draft saved before biographies were editable).
 */
const artistKeyedMapFromPayload = value =>
  value == null || typeof value !== "object" || Array.isArray(value)
    ? {}
    : value;

/**
 * Pure: unpack a saved draft payload into wizard state. Accepts both the
 * wizard's `instances` shape and (defensively) a plain `ids` payload.
 */
export const wizardStateFromDraftPayload = payload => {
  const instances = Array.isArray(payload?.instances) ? payload.instances : [];
  const orderedIds =
    instances.length > 0
      ? instances.map(entry => entry?.id).filter(id => id != null)
      : Array.isArray(payload?.ids)
        ? payload.ids
        : [];
  const idList = value => (Array.isArray(value) ? value : []);

  return {
    orderedIds,
    configurationsById: configurationsFromDraftPayload(payload),
    reportCover: reportCoverFromPayload(payload),
    // Existing drafts predate the switch, so omission means the cover is on.
    includeReportCover: payload?.includeReportCover !== false,
    includePrices: payload?.includePrices !== false,
    // All keyed by artist and shared by every work they appear in.
    biographies: artistKeyedMapFromPayload(payload?.biographies),
    artistPortraits: artistKeyedMapFromPayload(payload?.artistPortraits),
    portraitCaptions: artistKeyedMapFromPayload(payload?.portraitCaptions),
    // Where the cohort was picked, which is what the list calls the report's
    // type. Resuming or reissuing keeps it, so a brochure doesn't forget it
    // came from Artworks the first time it's saved again.
    artworkIds: idList(payload?.artworkIds),
    exhibitionIds: idList(payload?.exhibitionIds),
  };
};

/**
 * Pure: whether a wizard session opens with sale prices shown.
 *
 * A resumed draft is what it was saved as, whatever opened it: the Reporting
 * list knows nothing about where the cohort was picked, so letting the entry
 * point's default win here would quietly put prices back into a brochure that
 * was saved without them. Everything else starts where its entry point asked —
 * Artworks asks for them off, its works each having several instances to take a
 * price from.
 */
export const initialIncludePrices = ({
  draftPayload = null,
  includePrices = true,
} = {}) =>
  draftPayload != null
    ? draftPayload.includePrices !== false
    : includePrices !== false;

/**
 * Pure: build the `saveReportDraft` input from wizard state. The ordered
 * `instances` array is the source of truth — the server derives `ids` (and so
 * the render order) from it, and each entry carries that work's section in the
 * order it prints.
 *
 * A work the user never opened is saved with the section the wizard showed it
 * as, so what was on screen is what renders.
 *
 * Default-on switches are only sent when switched off. This keeps old drafts
 * compatible: omission means both the report cover and prices print.
 *
 * The artworks or exhibitions the cohort was picked from ride along so the
 * saved report remembers which module it was started in. They don't take part
 * in resolving it — `instances` already names every work, and the server
 * prefers those ids.
 *
 * The edited biographies, uploaded portraits and their captions are keyed by
 * artist rather than by work, and are only sent once there are some: an artist
 * reads the same on every biography page they get.
 */
export const buildDraftInput = ({
  id = null,
  orderedIds = [],
  configurationsById = {},
  reportCover = null,
  includeReportCover = true,
  includePrices = true,
  artworkIds = [],
  exhibitionIds = [],
  biographies = {},
  artistPortraits = {},
  portraitCaptions = {},
} = {}) => ({
  ...(id != null ? { id } : {}),
  type: "Instance",
  report: "brochure",
  format: "pdf",
  instances: orderedIds.map(instanceId => ({
    id: instanceId,
    pages: sectionPagesOf(configurationsById[instanceId], {
      multiInstance: orderedIds.length > 1,
    }).map(({ template, imageSlots, content, imageOrientation }) => ({
      template,
      imageSlots,
      ...(content ? { content } : {}),
      ...(imageOrientation ? { imageOrientation } : {}),
    })),
    publicCollections: configurationsById[instanceId]?.publicCollections || {},
  })),
  ...(reportCover != null ? { reportCover } : {}),
  ...(includeReportCover === false ? { includeReportCover: false } : {}),
  ...(includePrices === false ? { includePrices: false } : {}),
  ...(artworkIds.length > 0 ? { artworkIds } : {}),
  ...(exhibitionIds.length > 0 ? { exhibitionIds } : {}),
  ...(Object.keys(biographies || {}).length > 0 ? { biographies } : {}),
  ...(Object.keys(artistPortraits || {}).length > 0 ? { artistPortraits } : {}),
  ...(Object.keys(portraitCaptions || {}).length > 0
    ? { portraitCaptions }
    : {}),
});

/**
 * Pure: is every required image slot in this work's saved section filled?
 * Used for the per-instance ticks in the navigation list.
 *
 * Merely editing one page materialises the whole default section in `pages`, so
 * the presence of saved pages does not mean the work is complete. Optional
 * slots (the image on a Free Text page) do not hold the tick back.
 */
export const isInstanceConfigured = configuration => {
  const pages = configuration?.pages;
  if (!Array.isArray(pages) || pages.length === 0) return false;

  return pages.every(page =>
    templateSlots(page.template)
      .filter(slot => !slot.optional)
      .every(slot => page.imageSlots?.[slot.key] != null),
  );
};

/**
 * Pure: a short human summary of a work's section for the review step, e.g.
 * "Artwork Hero, Two Details ×2, Biography", counting a template that appears
 * more than once.
 */
export const sectionSummary = (pages = []) => {
  if (pages.length === 0) return "No pages";
  const counts = pages.reduce(
    (accumulator, page) =>
      accumulator.set(page.template, (accumulator.get(page.template) || 0) + 1),
    new Map(),
  );
  return [...counts.entries()]
    .map(([template, count]) =>
      count > 1
        ? `${templateLabel(template)} ×${count}`
        : templateLabel(template),
    )
    .join(", ");
};

/**
 * Pure: "3 of 5 slots picked" across a work's section, for the review step.
 * Unpicked slots fall back to the work's primary image server-side; the optional
 * ones simply print nothing, so they aren't counted here.
 */
export const sectionImagesSummary = (pages = []) => {
  const slots = pages.flatMap(page =>
    templateSlots(page.template)
      .filter(slot => !slot.optional)
      .map(slot => ({ page, slot })),
  );
  if (slots.length === 0) return "No images to pick";
  const picked = slots.filter(
    ({ page, slot }) => page.imageSlots?.[slot.key] != null,
  ).length;
  return picked === 0
    ? "Primary image in every slot"
    : `${picked} of ${slots.length} slots picked`;
};

/**
 * Pure: the cover's fields as one line of prose, for the rows that show the
 * cover without showing the cover.
 */
export const coverSummaryText = (cover = {}) => {
  const heading = [cover?.title, cover?.subtitle].filter(Boolean).join(" — ");
  const metadata = [cover?.dates || cover?.year, cover?.location]
    .filter(Boolean)
    .join(" — ");
  return [heading, metadata].filter(Boolean).join(", ") || "Untitled";
};

/**
 * Pure: suggest the report cover text from the cohort. Users can always
 * overtype — this is just a starting point.
 *
 * A single work covers itself: its artist, title and dates, which is what the
 * brochure has always printed on the cover of a one-work document. A cohort
 * takes a shared series as the title (with a shared artist as the subtitle), or
 * a shared artist as the title, and leaves dates/location to whoever knows them.
 */
export const suggestedCoverText = (summaries = []) => {
  const distinct = values => [
    ...new Set(values.map(value => (value || "").trim()).filter(Boolean)),
  ];

  if (summaries.length === 1) {
    const [work] = summaries;
    return {
      title: work?.artistName || "",
      subtitle: work?.title || "",
      dates: work?.year || "",
      location: "",
    };
  }

  const seriesList = distinct(summaries.map(summary => summary.series));
  const artists = distinct(summaries.map(summary => summary.artistName));
  const artistName = artists.length === 1 ? artists[0] : "";

  if (seriesList.length === 1) {
    return {
      title: seriesList[0],
      subtitle: artistName,
      dates: "",
      location: "",
    };
  }
  return { title: artistName, subtitle: "", dates: "", location: "" };
};

/**
 * Pure: filter instance summaries by a free-text query against the
 * reference, artist and title (case-insensitive). An empty query keeps all.
 */
export const filterInstanceSummaries = (summaries = [], query = "") => {
  const needle = (query || "").trim().toLowerCase();
  if (!needle) return summaries;
  return summaries.filter(summary =>
    [summary.name, summary.artistName, summary.title, summary.series]
      .filter(Boolean)
      .some(value => value.toLowerCase().includes(needle)),
  );
};
